import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";

import MainLayout from "../../../../components/layout/MainLayout";
import Breadcrumbs from "../../../../components/common/Breadcrumbs";
import ViolenceRiskForm from "./ViolenceRiskForm";

import {
    fetchViolenceRisk,
    updateViolenceRisk,
} from "../../../../store/slices/violenceRisksSlice";

const EditViolenceRisk = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const { state } = useLocation();

    const { caseId, patientId } = state || {};

    const [formData, setFormData] = useState({
        assessmentTiming: "",
        patientName: "",
        date: "",
        violentThreats: "",
        violentActs: "",
        substanceMisuse: "",
        psychoticSymptoms: "",
        personalityDisorder: "",
        nonCompliance: "",
        suspicion: "",
        lackEmpathy: "",
        unrealisticPlanning: "",
        futureStress: "",
        overallRisk: "",
        suggestedAction: "",
        justification: "",
    });

    // 🔁 snake_case → camelCase
    const mapFromApi = (data) => ({
        assessmentTiming: data.assessment_timing ?? "",
        patientName: data.patient_name ?? "",
        date: data.date.split('T')[0] ?? "",

        violentThreats: data.violent_threats ?? "",
        violentActs: data.violent_acts ?? "",
        substanceMisuse: data.substance_misuse ?? "",
        psychoticSymptoms: data.psychotic_symptoms ?? "",
        personalityDisorder: data.personality_disorder ?? "",
        nonCompliance: data.non_compliance ?? "",
        suspicion: data.suspicion ?? "",
        lackEmpathy: data.lack_empathy ?? "",
        unrealisticPlanning: data.unrealistic_planning ?? "",
        futureStress: data.future_stress ?? "",

        overallRisk: data.overall_risk ?? "",
        suggestedAction: data.suggested_action ?? "",
        justification: data.justification ?? "",
    });

    // 🔁 camelCase → snake_case
    const mapToApi = (data) => ({
        assessment_timing: data.assessmentTiming,
        patient_name: data.patientName,
        date: data.date,

        violent_threats: data.violentThreats,
        violent_acts: data.violentActs,
        substance_misuse: data.substanceMisuse,
        psychotic_symptoms: data.psychoticSymptoms,
        personality_disorder: data.personalityDisorder,
        non_compliance: data.nonCompliance,
        suspicion: data.suspicion,
        lack_empathy: data.lackEmpathy,
        unrealistic_planning: data.unrealisticPlanning,
        future_stress: data.futureStress,

        overall_risk: data.overallRisk,
        suggested_action: data.suggestedAction,
        justification: data.justification,
    });

    // ================= FETCH ON LOAD =================
    useEffect(() => {
        const loadData = async () => {
            try {
                const record = await dispatch(fetchViolenceRisk(id)).unwrap();
                setFormData(mapFromApi(record));
            } catch (error) {
                Swal.fire("Error", "Failed to load V-RISK-10 assessment.", "error");
            }
        };

        loadData();
    }, [id, dispatch]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // ================= UPDATE =================
    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const payload = mapToApi(formData);

            await dispatch(
                updateViolenceRisk({ case_id: caseId, violenceRiskData: payload })
            ).unwrap();

            Swal.fire(
                "Updated!",
                "V-RISK-10 assessment updated successfully.",
                "success"
            );

            navigate(`/cases/${caseId}/profile`, {
                state: { patientId, caseId },
            });
        } catch (error) {
            Swal.fire("Error", "Failed to update assessment.", "error");
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <Breadcrumbs />
                    <Link
                        to={`/optional/violence-risk/print/${id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-secondary"
                    >
                        <i className="ri-printer-line me-1"></i> Print
                    </Link>
                </div>

                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleSubmit}>
                            <ViolenceRiskForm
                                formData={formData}
                                handleChange={handleChange}
                            />
                            <div className="col-12 d-flex justify-content-end mt-3">
                                <div className="col-md-2 d-flex justify-content-end gap-2">
                                    <button type="submit" className="btn btn-primary px-4">
                                        <i className="ri-edit-2-line me-1"></i> Update
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditViolenceRisk;
